import React, { useState } from 'react';
import { Users, UserPlus, Search, Filter, Mail, Phone, MessageCircle, Pencil, Archive, MoreVertical } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Counter } from '../components/ui/Counter';
import { useNavigate } from 'react-router-dom';
import { useClients } from '../hooks/useClients';
import { ClientCard } from '../components/clients/ClientCard';
import { clientAPI } from '../api/clients';
import { useAgents } from '../hooks/useAgents';
import { cn } from '../lib/utils';

export const ActiveClients: React.FC = () => {
  const navigate = useNavigate();
  const { clients, isLoading, refetch } = useClients();
  const { agents } = useAgents();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'sale' | 'rent' | 'short-rent'>('all');
  const [filterInterest, setFilterInterest] = useState<'all' | 'very-interested' | 'interested' | 'to-contact'>('all');
  const [filterAgent, setFilterAgent] = useState<'all' | string>('all');
  const [activeMenu, setActiveMenu] = useState<number | null>(null);

  const activeClients = clients.filter(client => client.status === 'active');

  const filteredClients = activeClients.filter(client => {
    const matchesSearch = searchTerm === '' ||
      `${client.firstName} ${client.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.includes(searchTerm);

    const matchesType = filterType === 'all' || client.criteria?.transactionType === filterType;
    const matchesInterest = filterInterest === 'all' || client.interestLevel === filterInterest;
    const matchesAgent = filterAgent === 'all' || client.agent_name === filterAgent;

    return matchesSearch && matchesType && matchesInterest && matchesAgent;
  });

  const handleContact = (client: any, method: 'email' | 'phone' | 'whatsapp') => {
    switch (method) {
      case 'email':
        window.location.href = `mailto:${client.email}`;
        break;
      case 'phone':
        window.location.href = `tel:${client.phone}`;
        break;
      case 'whatsapp':
        const phone = client.phone.replace(/\s+/g, '').replace('+', '');
        window.open(`https://wa.me/${phone}`, '_blank');
        break;
    }
  };

  const handleArchive = async (clientId: number, clientName: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir archiver le client ${clientName} ?`)) {
      return;
    }
    try {
      await clientAPI.archive(clientId);
      alert('Client archivé avec succès');
      refetch();
    } catch (error) {
      console.error('Erreur lors de l\'archivage du client:', error);
      alert('Erreur lors de l\'archivage du client');
    }
  };

  const handleMenuToggle = (clientId: number) => {
    setActiveMenu(activeMenu === clientId ? null : clientId);
  };

  const handleMenuAction = (action: string, client: any) => {
    setActiveMenu(null);

    switch (action) {
      case 'edit':
        navigate(`/modifier/client/${client.id}`);
        break;
      case 'archive':
        handleArchive(client.id, `${client.firstName} ${client.lastName}`);
        break;
      case 'email':
        handleContact(client, 'email');
        break;
      case 'phone':
        handleContact(client, 'phone');
        break;
      case 'whatsapp':
        handleContact(client, 'whatsapp');
        break;
    }
  };

  const getInterestColor = (level: string) => {
    switch (level) {
      case 'very-interested':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'interested':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'to-contact':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const getInterestLabel = (level: string) => {
    switch (level) {
      case 'very-interested':
        return 'Très intéressé';
      case 'interested':
        return 'Intéressé';
      case 'to-contact':
        return 'À recontacter';
      default:
        return level;
    }
  };

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'sale':
        return 'Achat';
      case 'rent':
        return 'Location';
      case 'short-rent':
        return 'Location courte durée';
      default:
        return type;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-primary-500" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Clients Actifs
              </h1>
            </div>
            <Counter
              count={activeClients.length}
              label={activeClients.length > 1 ? "clients actifs" : "client actif"}
              variant="primary"
            />
          </div>
          <Button
            onClick={() => navigate('/ajouter/client')}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-5 w-5" />
            Ajouter un client
          </Button>
        </div>

        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="grid gap-4 md:grid-cols-5">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
              />
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-full"
            >
              <option value="all">Tous les types</option>
              <option value="sale">Achat</option>
              <option value="rent">Location</option>
              <option value="short-rent">Location courte durée</option>
            </select>
            <select
              value={filterInterest}
              onChange={(e) => setFilterInterest(e.target.value as any)}
              className="w-full"
            >
              <option value="all">Tous les niveaux</option>
              <option value="very-interested">Très intéressé</option>
              <option value="interested">Intéressé</option>
              <option value="to-contact">À recontacter</option>
            </select>
            <select
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              className="w-full"
            >
              <option value="all">Tous les commerciaux</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.name}>
                  {agent.name}
                </option>
              ))}
            </select>
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
              {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} trouvé{filteredClients.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-[200px] animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"
              />
            ))}
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-700">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Aucun client trouvé
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || filterType !== 'all' || filterInterest !== 'all'
                ? 'Aucun client ne correspond aux critères de recherche'
                : 'Commencez par ajouter vos premiers clients'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Client
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Contact
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Recherche
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Budget
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Intérêt
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Commercial
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {client.civility} {client.firstName} {client.lastName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {client.city}, {client.wilaya}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {client.phone}
                      </div>
                      {client.email && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {client.email}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {getTransactionLabel(client.criteria?.transactionType || '')}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {client.criteria?.propertyType || 'Non spécifié'}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {Math.floor(client.criteria?.minBudget || 0).toLocaleString('fr-FR').replace(/,/g, ' ')} DA
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        à {Math.floor(client.criteria?.maxBudget || 0).toLocaleString('fr-FR').replace(/,/g, ' ')} DA
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getInterestColor(client.interestLevel)}`}>
                        {getInterestLabel(client.interestLevel)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className={cn(
                        "text-sm font-medium",
                        client.agent_name === 'BENACHOUR Mehdi'
                          ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded-md'
                          : 'text-gray-900 dark:text-white'
                      )}>
                        {client.agent_name || 'Non assigné'}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <div className="relative">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleMenuToggle(client.id)}
                          title="Actions"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>

                        {activeMenu === client.id && (
                          <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/5">
                            <button
                              onClick={() => handleMenuAction('edit', client)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <Pencil className="h-4 w-4" />
                              Modifier
                            </button>
                            <button
                              onClick={() => handleMenuAction('archive', client)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <Archive className="h-4 w-4" />
                              Archiver
                            </button>
                            <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>
                            <button
                              onClick={() => handleMenuAction('email', client)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <Mail className="h-4 w-4" />
                              Envoyer un email
                            </button>
                            <button
                              onClick={() => handleMenuAction('phone', client)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <Phone className="h-4 w-4" />
                              Appeler
                            </button>
                            <button
                              onClick={() => handleMenuAction('whatsapp', client)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <MessageCircle className="h-4 w-4" style={{ color: '#25D366' }} />
                              WhatsApp
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
